import React from 'react';
import { cn } from '../../lib/utils';

/**
 * Page shell shared by every instrument (boards 18 and 20). One DOM tree that
 * rearranges itself at Tailwind `lg` (1024px), so nothing is rendered twice:
 *
 *  - Phone (< lg, board 18): one column filling the screen under the header. Title,
 *    board and notes row sit at the top; the controls panel follows after a fixed
 *    gap (the notes row's bottom margin) on every instrument, and stretches down to
 *    the bottom of the screen (rounded top, `panel` colour). A short board (mandolin)
 *    therefore moves the tools up instead of leaving a hole. Not fixed or sticky: on
 *    a short phone the page simply scrolls.
 *  - Desktop (lg+, board 20): a sidebar on the left (controls, quick buttons pinned
 *    to its bottom) and the title / board / notes in the main area on the right,
 *    centred with a max width.
 *
 * DOM order is main, then sidebar (`lg:order-first` puts the sidebar on the left), so a screen reader reads title, board, notes,
 * controls on both layouts. On desktop that means keyboard Tab reaches the title
 * arrows before the sidebar.
 *
 * Sizes: the sidebar is 360px wide. The slots do not know which layout is active;
 * pass `size` to their components via `useIsDesktop()`.
 *
 * @param {object} props
 * @param {React.ReactNode} props.title The <KeyTitle> (the page's <h1>).
 * @param {React.ReactNode} props.board The fretboard / fingerboard.
 * @param {React.ReactNode} props.notes The <NotesInKey> row.
 * @param {React.ReactNode} props.controls The <ControlCard>s (Key, Strings, ...).
 * @param {React.ReactNode} [props.quickButtons] <QuickButton>s only. The layout places them
 *   in an equal-width row under a hairline at the bottom of the controls panel.
 * @param {boolean} [props.notesBeside] For tall, vertical boards (fiddle, upright bass): the notes
 *   sit in a column to the right of the board instead of under it, on phone and desktop, so the
 *   page does not run far below the board. From xl (1280px) the board itself stays centred in the
 *   main area. Pass `<NotesInKey orientation="vertical">` to match.
 * @param {string} [props.className]
 */
export function InstrumentLayout({ title, board, notes, controls, quickButtons, notesBeside = false, className }) {
  return (
    <div
      className={cn(
        'flex min-h-[calc(100dvh-52px)] flex-1 flex-col lg:min-h-[calc(100dvh-68px)] lg:flex-row', // minus the header
        className,
      )}
    >
      <main className="flex min-w-0 flex-none flex-col pt-2.5 lp:pt-1 lg:flex-1 lg:items-center lg:px-8 lg:pb-10 lg:pt-[30px]">
        <div className="flex w-full flex-col lg:max-w-6xl">
          {title}
          {notesBeside ? (
            // Phone and lg: board + notes centre as a pair, notes in a slim column on the right (the
            // board shrinks a little on a narrow phone to make room). xl+: a 1fr | board | 1fr grid,
            // so the board stays centred on the page (like the title above it) with the notes in
            // the right-hand column; below xl a centred board would leave no room for them.
            <div className="mx-4 mb-4 mt-3 flex items-start justify-center gap-2 lg:mx-0 lg:mb-0 lg:mt-[18px] lg:gap-14 xl:grid xl:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] xl:gap-0">
              <div className="min-w-0 flex-initial lg:flex-none xl:col-start-2">{board}</div>
              {/* pt lines the heading up with the string names at the top of the board */}
              <div className="w-[76px] flex-none pt-1 lg:w-[200px] lg:pt-2 xl:ml-14">{notes}</div>
            </div>
          ) : (
            <>
              <div className="mx-4 mt-3 min-w-0 lp:mt-1.5 lg:mx-0 lg:mt-[18px]">{board}</div>
              <div className="mx-4 mb-4 mt-2.5 lg:mx-0 lg:mb-0 lg:mt-[18px]">{notes}</div>
            </>
          )}
        </div>
      </main>

      <aside
        aria-label="Controls"
        className={cn(
          'flex flex-1 flex-col gap-1.5 border-t border-line bg-panel px-2.5 pb-2 pt-2.5 max-[379px]:px-1.5',
          'rounded-t-2xl',
          'lg:order-first lg:w-[360px] lg:flex-none lg:gap-3 lg:rounded-none lg:border-r lg:border-t-0 lg:px-[18px] lg:pb-7 lg:pt-5',
        )}
      >
        <div className="flex flex-col gap-1.5 lg:gap-3">{controls}</div>
        {quickButtons ? (
          <div
            className={cn(
              'grid auto-cols-fr grid-flow-col border-t border-line',
              '-mx-2.5 px-2 pt-1.5 max-[379px]:-mx-1.5',
              'lg:mx-0 lg:mt-auto lg:px-0 lg:pt-2',
            )}
          >
            {quickButtons}
          </div>
        ) : null}
      </aside>
    </div>
  );
}
